
import { EventSponsor } from "@/types/EventSponsor";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import {
  listEventSponsorsPaginated,
  countEventSponsors as countEventSponsorsApi,
  exportEventSponsors as exportEventSponsorsApi,
  linkSponsorGuest,
  unlinkSponsorGuest,
} from "@/lib/api/clients/EventSponsorClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

/**
 * Wave A sponsor hub list — POST /event-sponsors/{eventId}/list. Page is
 * 0-based (guest/check-in convention); pageSize capped at 100 (mirrors the
 * resource-level cap so a bad caller fails fast client-side).
 */
export async function getEventSponsorsList(
  eventId: string,
  searchTerm: string,
  page: number,
  pageSize: number,
  filters: Filter[],
  sortBy: string = "sortIndex",
  sortOrder: "asc" | "desc" = "asc",
  conditions?: ConditionGroup,
): Promise<EventSponsor[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (pageSize < 1 || pageSize > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await listEventSponsorsPaginated(eventId, {
      searchTerm,
      page,
      pageSize,
      filters,
      sortBy,
      sortOrder,
      ...(conditions ? { conditions } : {}),
    });

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in getEventSponsorsList:", error);
    return handleErrorServer(error);
  }
}

export async function getEventSponsorsCount(
  eventId: string,
  filters: Filter[] = [],
  searchTerm: string = "",
  conditions?: ConditionGroup,
): Promise<number | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await countEventSponsorsApi(eventId, {
      searchTerm,
      filters,
      ...(conditions ? { conditions } : {}),
    });
  } catch (error) {
    console.error("Error in getEventSponsorsCount:", error);
    return handleErrorServer(error);
  }
}

/** Wave L: mark/unmark a guest as belonging to this sponsor. 409
 *  guestAlreadyLinkedToSponsor surfaces as a ServerError. */
export async function linkGuestToSponsor(
  eventId: string,
  sponsorId: string,
  guestId: string,
): Promise<void | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(sponsorId) ||
      !isValidObjectId(guestId)
    )
      throw new Error("validationError");
    return await linkSponsorGuest(eventId, sponsorId, guestId);
  } catch (error) {
    console.error("Error in linkGuestToSponsor:", error);
    return handleErrorServer(error);
  }
}

export async function unlinkGuestFromSponsor(
  eventId: string,
  sponsorId: string,
  guestId: string,
): Promise<void | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(sponsorId) ||
      !isValidObjectId(guestId)
    )
      throw new Error("validationError");
    return await unlinkSponsorGuest(eventId, sponsorId, guestId);
  } catch (error) {
    console.error("Error in unlinkGuestFromSponsor:", error);
    return handleErrorServer(error);
  }
}

/** Synchronous export (csv|excel) — filter/search-scoped per the contract. */
export async function exportEventSponsors(
  eventId: string,
  params: {
    format: "csv" | "excel";
    fields?: string;
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    columnLabels?: Record<string, string>;
  },
): Promise<Blob | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await exportEventSponsorsApi(eventId, params);
  } catch (error) {
    console.error("Error in exportEventSponsors:", error);
    return handleErrorServer(error);
  }
}
